import { useState, useRef, useCallback, type KeyboardEvent } from 'react'
import { Input, Button, Space, Upload, Tag, message as antMessage } from 'antd'
import { Send, StopCircle, Paperclip, X } from 'lucide-react'
import { useChatStore } from '@/store/chatStore'
import { useFileStore } from '@/store/fileStore'
import { useUploadFile } from '@/api/upload.api'
import { TagSelector } from '../TagSelector'
import { t } from '@/config/translations'
import { branding } from '@/config/branding'
import styles from './ChatInput.module.css'

const { TextArea } = Input

export interface ChatInputProps {
  onSend: (query: string) => void
  onStop: () => void
  isStreaming: boolean
  disabled?: boolean
}

export function ChatInput({ onSend, onStop, isStreaming, disabled = false }: ChatInputProps) {
  const [query, setQuery] = useState('')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const textAreaRef = useRef<any>(null)

  const { sessionId } = useChatStore()
  const { uploadedFiles, addFile, removeFile } = useFileStore()
  const uploadMutation = useUploadFile()

  const handleSend = useCallback(() => {
    const trimmedQuery = query.trim()
    if (!trimmedQuery || isStreaming) return

    onSend(trimmedQuery)
    setQuery('')
  }, [query, isStreaming, onSend])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      // Send on Enter (without Shift)
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend]
  )

  const handleFileUpload = useCallback(
    async (file: File) => {
      try {
        const result = await uploadMutation.mutateAsync({
          file,
          sessionId,
        })

        addFile({
          id: result.file_id,
          name: file.name,
          filename: file.name,
          size: file.size,
          type: 'text', // Default type
          file_id: result.file_id,
          uploaded_at: Date.now(),
        })

        void antMessage.success(t('file_uploaded_success', { name: file.name }))
      } catch (error) {
        console.error('File upload error:', error)
        void antMessage.error(t('file_upload_error', { name: file.name }))
      }
    },
    [sessionId, uploadMutation, addFile]
  )

  const handleBeforeUpload = useCallback(
    (file: File) => {
      void handleFileUpload(file)
      return false // Prevent default upload
    },
    [handleFileUpload]
  )

  const handleRemoveFile = useCallback(
    (fileId: string) => {
      removeFile(fileId)
      void antMessage.info(t('file_removed'))
    },
    [removeFile]
  )

  const isDemoMode = branding.isDemo

  return (
    <div className={styles['chatInput']}>
      {/* Tag selector */}
      {!isDemoMode && <TagSelector />}

      {/* File chips */}
      {uploadedFiles.length > 0 && (
        <div className={styles['fileChips']}>
          <Space wrap>
            {uploadedFiles.map((file) => (
              <Tag
                key={file.id}
                closable
                onClose={() => {
                  handleRemoveFile(file.id)
                }}
                closeIcon={<X size={14} />}
              >
                📎 {file.name}
              </Tag>
            ))}
          </Space>
        </div>
      )}

      {/* Input area */}
      <Space.Compact style={{ width: '100%' }}>
        {/* Hide file upload in demo mode */}
        {!isDemoMode && (
          <Upload
            beforeUpload={handleBeforeUpload}
            showUploadList={false}
            disabled={disabled || isStreaming}
            accept=".txt,.md,.pdf,.doc,.docx,.py,.js,.ts,.tsx,.jsx"
          >
            <Button
              icon={<Paperclip size={18} />}
              disabled={disabled || isStreaming}
              loading={uploadMutation.isPending}
              title={t('attach_file')}
            />
          </Upload>
        )}

        <TextArea
          ref={textAreaRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
          }}
          onKeyDown={handleKeyDown}
          placeholder={t('chat_input_placeholder')}
          disabled={disabled || isStreaming}
          autoSize={{ minRows: 1, maxRows: 6 }}
          style={{ resize: 'none' }}
        />

        {isStreaming ? (
          <Button
            type="primary"
            danger
            icon={<StopCircle size={18} />}
            onClick={onStop}
            disabled={disabled}
          >
            {t('stop')}
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<Send size={18} />}
            onClick={handleSend}
            disabled={disabled || !query.trim()}
          >
            {t('send')}
          </Button>
        )}
      </Space.Compact>
    </div>
  )
}
